import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Select from '@atlaskit/select'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import AddIcon from '@atlaskit/icon/glyph/add'
import SearchIcon from '@atlaskit/icon/glyph/search'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import PersonIcon from '@atlaskit/icon/glyph/person'
import UploadIcon from '@atlaskit/icon/glyph/upload'
import Avatar from '@atlaskit/avatar'
import { selectCurrentFolder, reorderScenariosAsync } from '../../store/slices/folderSlice'
import { ScenarioCard } from './ScenarioCard'
import { prefetchUsers, getCachedUserDetails } from '../../utils/userCache'
import { getUserInfo, isJiraConnect } from '../../utils/jiraContext'
import { ScenarioPanelSkeleton } from '../common/FolderBrowserSkeleton'

/**
 * SORTABLE SCENARIO WRAPPER
 *
 * Wraps ScenarioCard with sortable drag-and-drop functionality.
 * Provides drag handle context to child components.
 */
function SortableScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone,
  isDragDisabled
}) {
  // Use prefixed ID to distinguish scenarios from folders in drag events
  const sortableId = `scenario-${scenario.id}`

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: sortableId,
    disabled: isDragDisabled,
    data: {
      type: 'scenario',
      scenario: scenario
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ScenarioCard
        scenario={scenario}
        onClick={onClick}
        onEdit={onEdit}
        onMove={onMove}
        onDelete={onDelete}
        onClone={onClone}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragDisabled={isDragDisabled}
        isDragging={isDragging}
      />
    </div>
  )
}

/**
 * DRAGGABLE SCENARIO WRAPPER (move-to-folder only)
 *
 * Used when reordering is not meaningful for the current view ("All Scenarios"
 * or any active filter). Cards are draggable so the user can drop them onto a
 * folder in the sidebar, but they are NOT registered as droppables — so they
 * cannot become a reorder target, and sibling rows do not shuffle during drag.
 *
 * Mirrors SortableScenarioCard's drag-id format (`scenario-{id}`) and data
 * payload so the unified drag handler in FolderBrowser treats both modes
 * identically for the scenario→folder case.
 */
function DraggableScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone
}) {
  const draggableId = `scenario-${scenario.id}`

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useDraggable({
    id: draggableId,
    data: {
      type: 'scenario',
      scenario: scenario
    }
  })

  // Hide the source row while dragging — the DragOverlay (configured in
  // FolderBrowser) renders the visible drag preview that follows the cursor.
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    position: 'relative'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ScenarioCard
        scenario={scenario}
        onClick={onClick}
        onEdit={onEdit}
        onMove={onMove}
        onDelete={onDelete}
        onClone={onClone}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragDisabled={false}
        isDragging={isDragging}
      />
    </div>
  )
}

/**
 * Extract the accountId from a scenario's assignee field.
 * Handles both string format (just accountId) and object format ({ accountId, displayName, ... }).
 *
 * @param {Object} scenario - Scenario object with optional assignee field
 * @returns {string|null} The assignee's accountId or null if unassigned
 */
const getAssigneeAccountId = (scenario) => {
  if (typeof scenario.assignee === 'string') return scenario.assignee
  return scenario.assignee?.accountId || null
}

/**
 * Shared compact styles for all Select dropdowns in the filter bar.
 * Keeps all filter controls at a consistent 30px height with 13px font.
 */
const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '13px'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px'
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '13px'
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '30px'
  })
}

/**
 * SCENARIO PANEL COMPONENT
 *
 * Right panel of the folder browser showing scenarios.
 *
 * Features:
 * - Displays scenarios for the selected folder
 * - Shows "All Scenarios" when no folder selected
 * - Inline filter bar: search, priority, assignee, "Assigned to me", sort
 * - Each scenario shows name, assignee, priority, progress bar
 * - Drag-and-drop to reorder scenarios within folder
 * - Click scenario to navigate to scenario detail page
 */
function ScenarioPanel({
  scenarios = [],
  selectedFolderId,
  loading = false,
  onScenarioClick,
  onCreateScenario,
  onEditScenario,
  onDeleteScenario,
  onMoveScenario,
  onCloneScenario,
  onReorderScenarios,
  onImportFromLibrary,
  onImportFromRelease,
  releaseId
}) {
  const dispatch = useDispatch()
  const currentFolder = useSelector(selectCurrentFolder)

  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState(null)
  const [assigneeFilter, setAssigneeFilter] = useState(null)
  const [assignedToMe, setAssignedToMe] = useState(false)
  const [showToolbar, setShowToolbar] = useState(false)

  // Current user's accountId for "Assigned to me" filter
  const [currentUserAccountId, setCurrentUserAccountId] = useState(null)

  // Cache version counter to trigger re-render after user details are prefetched
  const [userCacheVersion, setUserCacheVersion] = useState(0)

  // Fetch current user's accountId on mount (needed for "Assigned to me" toggle)
  useEffect(() => {
    if (isJiraConnect()) {
      getUserInfo().then(user => {
        if (user?.accountId) {
          setCurrentUserAccountId(user.accountId)
        }
      })
    }
  }, [])

  // Prefetch all assignee user details when scenarios change
  // Uses bulk API call instead of N individual calls per card
  // Increments userCacheVersion after fetch so assigneeOptions re-derives with display names
  useEffect(() => {
    const accountIds = scenarios
      .map(s => getAssigneeAccountId(s))
      .filter(Boolean)
    const uniqueIds = [...new Set(accountIds)]
    if (uniqueIds.length > 0) {
      prefetchUsers(uniqueIds).then(() => {
        setUserCacheVersion(v => v + 1)
      })
    }
  }, [scenarios])

  // Priority filter options
  const priorityOptions = [
    { value: null, label: 'All Priorities' },
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' }
  ]

  /**
   * Build assignee filter options from scenario data.
   * Uses the user cache (populated by prefetchUsers) for display names.
   * Re-derives when scenarios change or cache is updated (via userCacheVersion).
   */
  const assigneeOptions = useMemo(() => {
    const assigneeMap = new Map()

    scenarios.forEach(s => {
      const accountId = getAssigneeAccountId(s)
      if (!accountId || assigneeMap.has(accountId)) return

      // Try to get display name and avatar from scenario object first, then from cache
      let displayName = null
      let avatarUrl = null
      if (typeof s.assignee === 'object' && s.assignee?.displayName) {
        displayName = s.assignee.displayName
        avatarUrl = s.assignee.avatarUrl || null
      }
      // Always check cache for most up-to-date info (populated by prefetchUsers)
      const cached = getCachedUserDetails(accountId)
      if (cached) {
        displayName = cached.displayName || displayName
        avatarUrl = cached.avatarUrl || avatarUrl
      }

      assigneeMap.set(accountId, {
        value: accountId,
        label: displayName || 'Unknown User',
        avatarUrl: avatarUrl
      })
    })

    // Sort alphabetically by display name for easier scanning
    return Array.from(assigneeMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [scenarios, userCacheVersion])

  /**
   * Custom option renderer for the assignee Select dropdown.
   * Shows avatar + display name for each option (Jira-style).
   * Same pattern used in TestScenarioEditModal, NewRunModal, EditRunModal.
   */
  const formatAssigneeOption = useCallback(({ label, avatarUrl, value }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {value ? (
        <Avatar size="xsmall" src={avatarUrl} name={label} />
      ) : (
        <Avatar size="xsmall" name="All" />
      )}
      <span style={{ fontSize: '13px' }}>{label}</span>
    </div>
  ), [])

  // Helper to get creation date from scenario
  // Uses createdOn field from API, falls back to id if not available
  const getCreationValue = (scenario) => {
    // Primary field name from API is createdOn
    const dateValue = scenario.createdOn

    if (dateValue) {
      const date = new Date(dateValue)
      // Return timestamp if valid date, otherwise fall back to id
      return isNaN(date.getTime()) ? (scenario.id || 0) : date.getTime()
    }

    // No date field - use id as proxy (newer records typically have higher IDs)
    return scenario.id || 0
  }

  // Filter and sort scenarios based on all active filters
  const filteredScenarios = useMemo(() => {
    let result = [...scenarios]

    // Apply search filter — match by name, description, or scenario key (e.g. "POJ-1")
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(s =>
        s.scenarioName?.toLowerCase().includes(query) ||
        s.description?.toLowerCase().includes(query) ||
        s.scenarioKey?.toLowerCase().includes(query)
      )
    }

    // Apply priority filter
    if (priorityFilter?.value) {
      result = result.filter(s => s.priority === priorityFilter.value)
    }

    // Apply assignee filter (from dropdown)
    if (assigneeFilter?.value) {
      result = result.filter(s => getAssigneeAccountId(s) === assigneeFilter.value)
    }

    // Apply "Assigned to me" filter
    if (assignedToMe && currentUserAccountId) {
      result = result.filter(s => getAssigneeAccountId(s) === currentUserAccountId)
    }

    // Apply the view-dependent implicit sort (no user-facing sort picker):
    //   - Inside a folder → sort by orderIndex (drag-and-drop order, folder-scoped).
    //   - "All Scenarios" → sort by creation date (newest first) because orderIndex is per-folder
    //     and mixing folders would produce a meaningless order.
    if (currentFolder) {
      result.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
    } else {
      result.sort((a, b) => getCreationValue(b) - getCreationValue(a))
    }

    return result
  }, [scenarios, searchQuery, priorityFilter, assigneeFilter, assignedToMe, currentUserAccountId, currentFolder])

  // Check if any filters are active (used for button state, count display, clear button)
  const hasActiveFilters = searchQuery.trim() || priorityFilter?.value || assigneeFilter?.value || assignedToMe

  // Reordering is only meaningful when the visible list matches the DB's folder-scoped
  // orderIndex sequence. That requires:
  //   1. A specific folder is selected (orderIndex is per-folder — "All Scenarios" mixes folders).
  //   2. No filters are active (filters show a subset, so drag positions would be wrong).
  // Note: this gates *reorder* only. Move-to-folder (dragging a scenario onto a folder
  // in the sidebar) is allowed in every view via the DraggableScenarioCard branch below.
  const canReorder = !!currentFolder && !hasActiveFilters

  // Note: DndContext is provided by parent FolderBrowser component
  // This component only provides SortableContext for reordering scenarios

  // Clear all filters. Sort is implicit (no user-facing picker) so there's nothing to reset.
  const handleClearFilters = () => {
    setSearchQuery('')
    setPriorityFilter(null)
    setAssigneeFilter(null)
    setAssignedToMe(false)
  }

  // Panel title: folder name when a folder is selected, otherwise the "All Scenarios" view label
  const panelTitle = currentFolder?.name || 'All Scenarios'

  // Get scenario IDs for SortableContext
  // Use prefixed IDs to match the sortable items
  const scenarioIds = filteredScenarios.map(s => `scenario-${s.id}`)

  // Loading state - shimmer skeleton mimicking scenario card rows
  if (loading) {
    return <ScenarioPanelSkeleton />
  }

  return (
    <div
      className="scenario-panel"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden',
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px'
      }}
    >
      {/* Panel Header */}
      <div
        className="scenario-panel-header"
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)'
        }}
      >
        {/* Title Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: (showToolbar || hasActiveFilters) ? '12px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <h2 style={{
              font: 'var(--ds-font-body)',
              fontWeight: 600,
              color: 'var(--ds-text, #172B4D)',
              margin: 0
            }}>
              {panelTitle}
            </h2>
            <span style={{
              fontSize: '12px',
              color: 'var(--ds-text-subtlest, #6B778C)'
            }}>
              {hasActiveFilters
                ? `(${filteredScenarios.length} of ${scenarios.length})`
                : `(${scenarios.length})`
              }
            </span>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Import Dropdown - Import from Library or Release */}
            {(onImportFromLibrary || onImportFromRelease) && (
              <DropdownMenu
                trigger={({ triggerRef, ...props }) => (
                  <Button
                    {...props}
                    ref={triggerRef}
                    appearance="default"
                    iconBefore={<UploadIcon label="Import" size="small" />}
                  >
                    Import
                  </Button>
                )}
                shouldRenderToParent
              >
                <DropdownItemGroup>
                  {onImportFromLibrary && (
                    <DropdownItem onClick={onImportFromLibrary}>
                      Import from Library
                    </DropdownItem>
                  )}
                  {onImportFromRelease && (
                    <DropdownItem onClick={onImportFromRelease}>
                      Import from Release
                    </DropdownItem>
                  )}
                </DropdownItemGroup>
              </DropdownMenu>
            )}

            {/* Filter/Search Toggle Button */}
            <Button
              appearance={(showToolbar || hasActiveFilters) ? 'primary' : 'default'}
              iconBefore={<SearchIcon label="Search and filter" size="small" />}
              onClick={() => setShowToolbar(!showToolbar)}
              isSelected={showToolbar || hasActiveFilters}
            >
              {hasActiveFilters ? 'Filtered' : 'Search'}
            </Button>
          </div>
        </div>

        {/* Inline Filter & Sort Bar - Single row, Jira-style */}
        {(showToolbar || hasActiveFilters) && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Search Input */}
            <div style={{ flex: '1 1 160px', maxWidth: '200px', minWidth: '120px' }}>
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                elemBeforeInput={
                  <div style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon label="Search" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
                  </div>
                }
                isCompact
              />
            </div>

            {/* Priority Filter */}
            <div style={{ width: '130px', flexShrink: 0 }}>
              <Select
                options={priorityOptions}
                value={priorityFilter || priorityOptions[0]}
                onChange={(option) => setPriorityFilter(option?.value ? option : null)}
                isCompact
                isClearable={false}
                placeholder="Priority"
                styles={compactSelectStyles}
              />
            </div>

            {/* Assignee Filter - with Jira-style avatar rendering */}
            <div style={{ width: '170px', flexShrink: 0 }}>
              <Select
                options={[
                  { value: null, label: 'All Assignees', avatarUrl: null },
                  ...assigneeOptions
                ]}
                value={assigneeFilter || { value: null, label: 'All Assignees', avatarUrl: null }}
                onChange={(option) => setAssigneeFilter(option?.value ? option : null)}
                formatOptionLabel={formatAssigneeOption}
                isCompact
                isClearable={false}
                placeholder="Assignee"
                styles={compactSelectStyles}
              />
            </div>

            {/* "Assigned to me" Toggle Button - only shows when current user is known */}
            {currentUserAccountId && (
              <Button
                appearance={assignedToMe ? 'primary' : 'subtle'}
                iconBefore={<PersonIcon label="Assigned to me" size="small" />}
                onClick={() => setAssignedToMe(prev => !prev)}
                isSelected={assignedToMe}
                spacing="compact"
              >
                Me
              </Button>
            )}

            {/* Clear Filters Button - only visible when filters are active */}
            {hasActiveFilters && (
              <Button
                appearance="subtle"
                onClick={handleClearFilters}
                spacing="compact"
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Scenario List with Drag-and-Drop */}
      <div
        className="scenario-list"
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '0'
        }}
      >
        {filteredScenarios.length > 0 ? (
          canReorder ? (
            // Reorder-capable view: SortableContext + verticalListSortingStrategy
            // give the cards sibling-shuffle behavior during drag, and dropping
            // on another scenario triggers reorder via FolderBrowser CASE 3.
            <SortableContext
              items={scenarioIds}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {filteredScenarios.map(scenario => (
                  <SortableScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    onClick={() => onScenarioClick(scenario.id)}
                    onEdit={onEditScenario}
                    onMove={onMoveScenario}
                    onDelete={onDeleteScenario}
                    onClone={onCloneScenario}
                    isDragDisabled={false}
                  />
                ))}
              </div>
            </SortableContext>
          ) : (
            // Move-to-folder-only view ("All Scenarios" or active filters):
            // no SortableContext, so scenarios are not droppable on each other
            // (no reorder, no sibling shuffle). Folder rows in the sidebar are
            // independent useDroppable targets, so dragging onto a folder still
            // routes through FolderBrowser CASE 1 (moveScenarioToFolderAsync).
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {filteredScenarios.map(scenario => (
                <DraggableScenarioCard
                  key={scenario.id}
                  scenario={scenario}
                  onClick={() => onScenarioClick(scenario.id)}
                  onEdit={onEditScenario}
                  onMove={onMoveScenario}
                  onDelete={onDeleteScenario}
                  onClone={onCloneScenario}
                />
              ))}
            </div>
          )
        ) : scenarios.length > 0 ? (
          /* No Results from Filter */
          <div
            className="empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center',
              height: '100%'
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <SearchIcon
                label="No results"
                size="medium"
                primaryColor="var(--ds-text-subtlest, #6B778C)"
              />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 8px 0'
            }}>
              No matching scenarios
            </h3>

            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '0 0 20px 0',
              maxWidth: '280px'
            }}>
              Try adjusting your search or filter criteria.
            </p>

            <Button
              appearance="subtle"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          /* Empty State - No Scenarios */
          <div
            className="empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center',
              height: '100%'
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <DocumentsIcon
                label="Scenarios"
                size="medium"
                primaryColor="var(--ds-text-subtlest, #6B778C)"
              />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 8px 0'
            }}>
              {selectedFolderId === null
                ? 'No scenarios yet'
                : 'This folder is empty'}
            </h3>

            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '0 0 20px 0',
              maxWidth: '280px'
            }}>
              {selectedFolderId === null
                ? 'Create your first test scenario to get started with testing.'
                : 'Drag scenarios here or create new ones.'}
            </p>

            <Button
              appearance="primary"
              iconBefore={<AddIcon label="Add" size="small" />}
              onClick={onCreateScenario}
            >
              Add Scenario
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export { ScenarioPanel }
